import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import PhoneFrame from './phone_frame';
import ContentRenderer from './evangile/ContentRenderer';
import { useLatestEvangile } from '../hooks/useLatestEvangile';

interface DailyCommentaryCardProps {
  lang: string;
}

/**
 * Vue « écran mobile » du commentaire du jour (Évangile du jour + réflexion),
 * ou du dernier publié. Fallback anglais si la langue n'a pas de commentaire.
 */
const DailyCommentaryCard: React.FC<DailyCommentaryCardProps> = ({ lang }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { item, loading, effectiveLang } = useLatestEvangile(lang);

  // Squelette discret pendant le chargement
  if (loading) {
    return (
      <PhoneFrame className="mx-auto">
        <div className="h-full w-full animate-pulse bg-gradient-to-b from-amber-100 to-amber-200" />
      </PhoneFrame>
    );
  }

  // Aucun commentaire disponible (même en anglais) → la colonne se replie
  if (!item) return null;

  const detailUrl = `/${effectiveLang}/evangile/${item.date}`;
  const dateLabel = (() => {
    try {
      return new Date(`${item.date}T00:00:00`).toLocaleDateString(effectiveLang, {
        weekday: 'long',
        day: 'numeric',
        month: 'long',
      });
    } catch {
      return item.date;
    }
  })();

  const goToDetail = (e: React.MouseEvent) => {
    e.preventDefault();
    navigate(detailUrl);
  };

  const image = item.portraitThumbUrl || item.thumbUrl;

  return (
    <PhoneFrame className="mx-auto">
      <div className="flex h-full w-full flex-col bg-amber-50">
        {/* Bandeau image cliquable */}
        {image && (
          <a href={detailUrl} onClick={goToDetail} className="relative block shrink-0">
            <img
              src={image}
              alt={item.accroche}
              className="h-28 w-full object-cover"
              loading="lazy"
            />
            <span className="absolute left-2 top-2 rounded-full bg-amber-900/85 px-2 py-[2px] text-[0.6rem] font-bold uppercase tracking-wider text-amber-50">
              {t('home.hero.dailyCommentary', 'Commentaire du jour')}
            </span>
          </a>
        )}

        {/* En-tête : date + référence */}
        <div className="flex items-center justify-between gap-2 px-3 pt-2">
          <span className="text-[0.6rem] font-bold uppercase tracking-wider text-amber-600">
            {dateLabel}
          </span>
          {item.reference && (
            <span className="rounded-full border border-amber-200 bg-amber-100 px-2 py-[1px] text-[0.6rem] font-bold text-amber-900 whitespace-nowrap">
              {item.reference}
            </span>
          )}
        </div>

        {/* Accroche */}
        <a
          href={detailUrl}
          onClick={goToDetail}
          className="block px-3 pt-1 font-serif text-[0.9rem] italic leading-snug text-amber-950 no-underline"
        >
          {item.accroche}
        </a>

        {/* Corps du commentaire, clippé avec un fondu en bas */}
        <div className="relative mt-1 flex-1 overflow-hidden px-3 text-[0.72rem] leading-snug text-amber-900">
          <ContentRenderer text={item.contenu} locale={effectiveLang} />
          <div className="pointer-events-none absolute inset-x-0 bottom-0 h-16 bg-gradient-to-t from-amber-50 to-transparent" />
        </div>

        {/* Bouton Lire */}
        <div className="shrink-0 p-3">
          <a
            href={detailUrl}
            onClick={goToDetail}
            className="flex h-9 w-full items-center justify-center rounded-lg bg-amber-900 text-[0.8rem] font-medium text-white no-underline transition-colors hover:bg-amber-800"
          >
            {t('evangile.lire', 'Lire')}
          </a>
        </div>
      </div>
    </PhoneFrame>
  );
};

export default DailyCommentaryCard;
